/**
 * OnboardingProvider — single source of truth for the in-app tour.
 *
 * Owns:
 *   • Server state (TanStack Query) for /v1/onboarding
 *   • The current step (first not-completed step in the user's flow)
 *   • A target-element registry shared by all `useOnboardingStep` hooks
 *
 * markDone is idempotent — calling for an already-completed step
 * short-circuits and never round-trips.
 */
import {
  createContext, useCallback, useContext, useMemo, useRef,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FlowKey, OnboardingState } from '@builder/shared';
import { useAuth } from '@/store/auth.store';
import { onboardingApi } from '@/api/onboarding';
import { FLOW_STEPS } from './flows';
import type { StepDef } from './types';

interface OnboardingCtx {
  state: OnboardingState | null;
  steps: StepDef[];
  currentStep: StepDef | null;
  currentIndex: number;
  totalSteps: number;
  isActive: boolean;
  markDone: (stepId: string) => void;
  goNext: () => void;
  goPrev: () => void;
  dismiss: () => void;
  restart: () => void;
  registerTarget: (attr: string, el: HTMLElement | null) => void;
  getTarget: (attr: string) => HTMLElement | null;
}

const Ctx = createContext<OnboardingCtx | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }): JSX.Element {
  const user = useAuth((s) => s.user);
  const qc = useQueryClient();
  const targetsRef = useRef<Map<string, HTMLElement>>(new Map());

  // Don't fetch for client/broker — they live in the owner portal, not Builder.
  const enabled = Boolean(user) && user?.role !== 'client' && user?.role !== 'broker';

  // WWWWW · 2026-05-16 + ZZZZZ · 2026-05-16
  //   The onboarding endpoint is non-essential — never let it spam the
  //   network tab. retry=false stops the default 3× retry storm on any
  //   transient failure, and we widen staleTime so the same answer sticks
  //   around the whole session. ZZZZZ adds a queryFn-level catch so that
  //   ANY error (403, 500, network) becomes a silent `null` instead of
  //   bubbling up to react-query's error state — the auditor was seeing
  //   403 noise on every page that this catch suppresses defensively.
  const { data: state } = useQuery({
    queryKey: ['onboarding'],
    queryFn: async () => {
      try {
        return await onboardingApi.getState();
      } catch (err) {
        // Silently swallow. Tour is best-effort; nothing depends on it.
        return null as unknown as OnboardingState;
      }
    },
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const markMutation = useMutation({
    mutationFn: onboardingApi.markStep,
    onSuccess: (updated) => qc.setQueryData(['onboarding'], updated),
  });

  const dismissMutation = useMutation({
    mutationFn: onboardingApi.dismiss,
    onSuccess: () =>
      qc.setQueryData<OnboardingState | undefined>(['onboarding'], (old) =>
        old ? { ...old, dismissedAt: new Date().toISOString() } : old,
      ),
  });

  const steps = useMemo<StepDef[]>(
    () => (state ? (FLOW_STEPS[state.flowKey as FlowKey] ?? []) : []),
    [state],
  );

  const completedSet = useMemo(
    () => new Set(state?.completedSteps ?? []),
    [state],
  );

  const currentStep = useMemo(
    () => steps.find((s) => !completedSet.has(s.id)) ?? null,
    [steps, completedSet],
  );

  const currentIndex = useMemo(
    () => (currentStep ? steps.findIndex((s) => s.id === currentStep.id) : -1),
    [steps, currentStep],
  );

  const isActive = Boolean(
    state && !state.dismissedAt && !state.completedAt && currentStep,
  );

  const markDone = useCallback(
    (stepId: string) => {
      if (!completedSet.has(stepId)) markMutation.mutate(stepId);
    },
    [completedSet, markMutation],
  );

  const goNext = useCallback(() => {
    if (currentStep) markDone(currentStep.id);
  }, [currentStep, markDone]);

  const goPrev = useCallback(() => {
    // Cosmetic-only: pop the most recently completed step from the
    // local cache so the engine reverts to the previous step. Doesn't
    // hit the server; the next markStep call will reconcile.
    if (!state || state.completedSteps.length === 0) return;
    const prev = [...state.completedSteps];
    prev.pop();
    qc.setQueryData<OnboardingState>(['onboarding'], { ...state, completedSteps: prev });
  }, [state, qc]);

  const dismiss = useCallback(() => dismissMutation.mutate(), [dismissMutation]);

  const restart = useCallback(() => {
    if (!state) return;
    qc.setQueryData<OnboardingState>(['onboarding'], {
      ...state, completedSteps: [], dismissedAt: null, completedAt: null,
    });
  }, [state, qc]);

  const registerTarget = useCallback((attr: string, el: HTMLElement | null) => {
    if (el) targetsRef.current.set(attr, el);
    else targetsRef.current.delete(attr);
  }, []);

  const getTarget = useCallback(
    (attr: string) => targetsRef.current.get(attr) ?? null,
    [],
  );

  const value = useMemo<OnboardingCtx>(
    () => ({
      state: state ?? null,
      steps,
      currentStep,
      currentIndex,
      totalSteps: steps.length,
      isActive,
      markDone,
      goNext,
      goPrev,
      dismiss,
      restart,
      registerTarget,
      getTarget,
    }),
    [state, steps, currentStep, currentIndex, isActive, markDone, goNext, goPrev, dismiss, restart, registerTarget, getTarget],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOnboarding(): OnboardingCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useOnboarding must be used inside OnboardingProvider');
  return ctx;
}
